import {SelectProvider} from '@ariakit/react'
import {useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {SelectList, SelectPopover, SelectVirtualList} from '@/components/ui/select-atoms/SelectAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {MedicalReportDateByProject} from '@/features/medical-reports/types'
import {formatLocaleDate} from '@/dayjs'
import {
    StyledSelectItem,
    StyledSelectTrigger
} from '@/features/medical-reports/components/input-select-report-date-by-patient/style'
import {useReportDatesListByPatient} from '../../hooks/useReportDatesListByPatient'

interface InputSelectReportDateByPatientProps {
    patientId: string
    userId: string
    medicalRecordReportTypeId: string
    disabled?: boolean
    label?: string
    value: MedicalReportDateByProject['date'] | null
    onChange: (value: MedicalReportDateByProject | null) => void
    placeholder?: string
    errorMessage?: string
    onClear?: () => void
}

export const InputSelectReportDateByPatient = ({
    patientId,
    userId,
    disabled,
    label,
    onChange,
    errorMessage,
    placeholder,
    onClear,
    value,
    medicalRecordReportTypeId
}: InputSelectReportDateByPatientProps) => {
    const {t} = useTranslation()
    const [isQueryEnabled, setIsQueryEnabled] = useState(false)
    const {reportDates, isLoading, isError, hasNextPage, fetchNextPage} = useReportDatesListByPatient({
        patientId,
        userId,
        medicalRecordReportTypeId,
        enabled: !disabled && isQueryEnabled
    })

    return (
        <SelectProvider
            value={value ?? ''}
            setValue={value => onChange(reportDates.find(date => date.date === value) ?? null)}
            setOpen={isOpen => {
                setIsQueryEnabled(isOpen)
            }}
        >
            <Flexbox direction="column" gap={1.5} fullWidth>
                {label && <Label>{label}</Label>}
                <StyledSelectTrigger
                    placeholder={placeholder ?? t('commons:select')}
                    fullWidth
                    value={value ?? ''}
                    displayValue={() => (
                        <Flexbox justify="space-between" align="center" gap={2} fullWidth>
                            {value && formatLocaleDate(value, 'MMM YYYY')}
                            {onClear && (
                                <XCloseIcon
                                    role="button"
                                    onClick={e => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        onClear()
                                    }}
                                />
                            )}
                        </Flexbox>
                    )}
                    hasError={!!errorMessage}
                    disabled={disabled}
                />

                {errorMessage && (
                    <Flexbox align="center" gap={1.5}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                )}

                <SelectPopover sameWidth>
                    <SelectList>
                        {isLoading ? (
                            Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                        ) : isError ? (
                            <InputHelpText error={t('errors:default')} />
                        ) : reportDates.length ? (
                            <SelectVirtualList
                                data={reportDates}
                                totalCount={reportDates.length}
                                overscan={100}
                                endReached={() => hasNextPage && fetchNextPage()}
                                itemContent={(itemIndex: number, item: MedicalReportDateByProject) => (
                                    <StyledSelectItem hasItemCheckbox key={itemIndex} value={item.date}>
                                        {formatLocaleDate(item.date, 'MMM YYYY')}
                                    </StyledSelectItem>
                                )}
                            />
                        ) : (
                            <InputHelpText helpText={t('commons:no_results')} />
                        )}
                    </SelectList>
                </SelectPopover>
            </Flexbox>
        </SelectProvider>
    )
}
